import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type ForwardedRef,
  type ReactNode,
} from "react";
import {
  ticketEndpointContextEnum,
  getKeysThatHoldsValue,
  getKeysDisplayForMapping,
  getDataSourceIcon,
  systemOptionsEnum,
} from "../TicketingUtils";
import {
  useForm,
  FormProvider,
  useWatch,
  type UseFormReturn,
  type FieldValues,
} from "react-hook-form";
import TicketProviders from "../TicketProviders/TicketProviders";
import TicketEndpointKeys from "../TicketEndpointKeys/TicketEndpointKeys";
import TicketEndpointFields from "../TicketEndpointFields/TicketEndpointFields";
import {
  Loader,
  useIcon,
  TextButton,
  getAorAN,
  AlertIndicator,
  alertTypes,
} from "@SeemplicityDev/shared";
import { useApolloClient } from "@apollo/client";
import useTicketEndpointMutationData from "../useTicketEndpointMutationData/useTicketEndpointMutationData";
import TicketEndpointColumns, {
  ticketEndpointColumnsModesEnum,
} from "../TicketEndpointColumns/TicketEndpointColumns";
import type { EndpointConfig } from "../TicketingAPI";
import "./TicketTemplate.scss";

// Imperative handle exposed via `useImperativeHandle`. Kept compatible with
// `InternalManualTicketFormHandle` (useInternalManualTicketForm.tsx), which
// declares the same `{ createTicket: () => void }` shape.
export interface TicketTemplateHandle {
  createTicket: () => void;
}

// react-hook-form values managed by this form. `keys` is keyed by field name to
// a selected key id; `fields` values are heterogeneous and narrowed downstream
// (useTicketEndpointMutationData), so they are typed loosely here.
export interface TicketTemplateFormValues {
  ticketProvider: string | null;
  keys: Record<string, string>;
  fields: Record<string, unknown>;
}

// Argument shape passed to `onCreateTicket`. `formData` is the react-hook-form
// values; `mutationData` is the serialized mutation payload (JSON string).
// `formData` is exposed with optional fields + an index signature: consumers
// read varying optional fields off it (e.g. `ticketProvider`, `fields.duedate`,
// `queueSize`), narrowing at their own sites. The runtime object is the full
// `TicketTemplateFormValues` (cast at the call site).
export interface OnCreateTicketArgs {
  formData: {
    ticketProvider?: string;
    keys?: Record<string, string>;
    fields?: Record<string, unknown>;
    [key: string]: unknown;
  };
  mutationData: string;
}

export interface TicketTemplateProps {
  // TODO: filters_config flows through to a GraphQL FiltersConfigInput; typed as
  // unknown to match the package convention until that input shape is modeled.
  filters_config?: unknown;
  ticketManagerId?: string;
  ticketManagerFriendlyName?: string;
  ticketManagerMustHaveMapping?: boolean;
  ticketManagerMustHaveColumnsMapping?: boolean;
  // TODO: ticketManagerProviders originates from untyped .js callers; typed as a
  // minimal id-bearing list until the upstream provider shape is modeled.
  ticketManagerProviders?: Array<{ id?: string }>;
  ticketProviderId?: string;
  // TODO: keys originates from untyped .js callers; fed into form `keys`
  // (Record<string, string>). Typed as unknown until the upstream shape is modeled.
  keys?: unknown;
  fieldMappingId?: string;
  onEventTriggered?: (...args: unknown[]) => void;
  onCreateMapping?: (...args: unknown[]) => void;
  onCreateMappingError?: (...args: unknown[]) => void;
  wrapperClassName?: string;
  // TODO: initialValues flows into TicketEndpointFields (still .js); typed as
  // unknown until that component is converted.
  initialValues?: unknown;
  disableInitialValues?: boolean;
  onCreateTicket?: (args: OnCreateTicketArgs) => void;
  modalButtonsContainer?: ReactNode;
  onValidationChange?: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  externalEndpointId?: string;
  context?: (typeof ticketEndpointContextEnum)[keyof typeof ticketEndpointContextEnum];
  // TODO: fetchPolicy is an Apollo WatchQueryFetchPolicy passed through to
  // TicketEndpointFields (still .js); typed as string until that is converted.
  fetchPolicy?: string;
  additionalPreferences?: ReactNode;
  /* TODO: temporary - should be removed after integrating SNOW */
  withColumnMapping?: boolean;
  errorsContainer?: ReactNode;
  onMappingModalOpen?: (isOpen: boolean) => void;
  systemLabel?: (typeof systemOptionsEnum)[keyof typeof systemOptionsEnum];
  // TODO: endpointConfig flows into TicketEndpointFields (still .js); typed as
  // unknown until that component is converted.
  endpointConfig?: unknown;
}

const TicketTemplate = forwardRef<TicketTemplateHandle, TicketTemplateProps>(
  (
    {
      filters_config,
      ticketManagerId,
      ticketManagerFriendlyName,
      ticketManagerMustHaveMapping,
      ticketManagerMustHaveColumnsMapping,
      ticketManagerProviders,
      ticketProviderId,
      keys,
      fieldMappingId,
      onEventTriggered = () => {},
      onCreateMapping = () => {},
      onCreateMappingError = () => {},
      wrapperClassName,
      initialValues,
      disableInitialValues = false,
      onCreateTicket = () => {},
      modalButtonsContainer = null,
      onValidationChange = () => {},
      onDirtyChange = () => {},
      externalEndpointId,
      context = ticketEndpointContextEnum.MANUAL_TICKET,
      fetchPolicy,
      additionalPreferences,
      /* TODO: temporary - should be removed after integrating SNOW */
      withColumnMapping = true,
      errorsContainer = null,
      onMappingModalOpen = () => {},
      systemLabel = systemOptionsEnum.SEEMPLICITY,
      endpointConfig,
    }: TicketTemplateProps,
    ref: ForwardedRef<TicketTemplateHandle>
  ) => {
    const methods = useForm<TicketTemplateFormValues>({
      mode: "all",
      defaultValues: {
        ticketProvider: null,
        keys: {},
        fields: {},
      },
    });
    const {
      control,
      reset: formReset,
      formState: { isValid, isDirty },
      handleSubmit,
      watch,
      getValues,
    } = methods;

    const [keysFinalPhase, setKeysFinalPhase] = useState(false);
    const [keysHasMapping, setKeysHasMapping] = useState(false);
    const [keysError, setKeysError] = useState<{ message?: string } | null>(
      null
    );
    const [fieldsLoading, setFieldsLoading] = useState(false);
    const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
    // When generating a ticket from RMQ, it's essential to have the field mapping ID for the fields to be displayed.
    // These fields should align with the user's initial configuration when creating the RMQ.
    // It's important to note that the fields cannot be determined solely by the selected keys, as each RMQ maintains its unique field mapping.
    // Therefore, if a user modifies a key in the form,
    // they should receive the fields based on the updated set of keys rather than relying on the fieldMappingId.
    // So in the event of a key change, this variable will be set to null.
    const [fieldMappingProp, setFieldMappingProp] = useState<
      string | undefined
    >(fieldMappingId);
    const client = useApolloClient();
    const { getSvg } = useIcon();

    useEffect(() => {
      onDirtyChange(isDirty);
    }, [isDirty]);

    useEffect(() => {
      onMappingModalOpen(isMappingModalOpen);
    }, [isMappingModalOpen]);

    useImperativeHandle(ref, () => ({
      createTicket: () => {
        handleSubmit(onSubmit)();
      },
    }));

    const resetKeysStateVariables = () => {
      setKeysFinalPhase(false);
      setKeysHasMapping(false);
      // NOTE: original passes `false` (a boolean) where keysError state is
      // object|null. Preserved exactly; flagged as a bug candidate in report.
      setKeysError(false as unknown as { message?: string } | null);
      setFieldsReady(false);
    };

    useEffect(() => {
      formReset();
    }, [ticketManagerId]);

    const {
      getPropsFromField: getPropsFromKeyField,
      setGetPropsFromField: setGetPropsFromKeyField,
      getMutationArray: getKeysMutationArray,
    } = useTicketEndpointMutationData();
    const { setGetPropsFromField, getMutationArray: getFieldsMutationArray } =
      useTicketEndpointMutationData();
    const selectedTicketProvider = useWatch({
      control,
      name: `ticketProvider`,
    });

    useEffect(() => {
      if (ticketManagerProviders?.length === 1 && !ticketProviderId) {
        formReset({
          ...getValues(),
          ticketProvider: ticketManagerProviders?.[0]?.id ?? null,
        });
      }
    }, [ticketManagerProviders]);

    useEffect(() => {
      if (!!ticketProviderId) {
        formReset({ ...getValues(), ticketProvider: ticketProviderId });
      }
    }, [ticketProviderId]);

    useEffect(() => {
      if (!!keys) {
        formReset({
          ...getValues(),
          keys: keys as Record<string, string>,
        });
      }
    }, [keys]);

    const selectedKeys = watch("keys");

    const keysThatHoldsValue = getKeysThatHoldsValue(selectedKeys);

    const onSubmit = (formData: TicketTemplateFormValues) => {
      const mutationData = JSON.stringify(
        getKeysMutationArray(formData?.keys)
          .concat(getFieldsMutationArray(formData?.fields))
          ?.reduce<Record<string, unknown>>(
            (acc, obj) => ({ ...acc, [obj.key]: obj.value }),
            {}
          )
      );

      // `formData.ticketProvider` is `string | null` at runtime; the consumer
      // arg type uses optional `string`. Cast is type-only — same object.
      onCreateTicket({
        formData: formData as OnCreateTicketArgs["formData"],
        mutationData,
      });
    };

    const isEditMapping = keysFinalPhase && keysHasMapping && withColumnMapping;
    const isCreateMapping =
      keysFinalPhase && !keysHasMapping && withColumnMapping;

    const firstUndefinedKey =
      Object.keys(selectedKeys)?.length > 0 &&
      !!getPropsFromKeyField &&
      getPropsFromKeyField(
        Object.keys(selectedKeys)?.find((key) => !selectedKeys[key]) as string
      )?.label;
    const shouldShowKeysPlaceholder = !keysFinalPhase && !!firstUndefinedKey;
    const shouldShowKeysLoader =
      !keysFinalPhase &&
      !firstUndefinedKey &&
      Object.keys(selectedKeys)?.length > 0;
    const shouldShowFields =
      keysFinalPhase &&
      ((!!ticketManagerMustHaveMapping && keysHasMapping) ||
        !ticketManagerMustHaveMapping);
    const shouldShowSeparator =
      !(isCreateMapping && ticketManagerMustHaveMapping) &&
      !!Object.keys(selectedKeys)?.length;
    const [fieldsReady, setFieldsReady] = useState(false);

    // This useEffect triggers "onValidationChange" event on each form validation change in order
    // to expose this state to the parent component (holds the form buttons).
    useEffect(() => {
      const isValidForm =
        isValid &&
        keysFinalPhase &&
        !fieldsLoading &&
        (!isCreateMapping || !ticketManagerMustHaveMapping);
      onValidationChange(isValidForm);
    }, [
      fieldsLoading,
      isCreateMapping,
      isValid,
      keysFinalPhase,
      onValidationChange,
      ticketManagerMustHaveMapping,
    ]);

    const keysDisplayForMapping = getKeysDisplayForMapping(
      keysThatHoldsValue,
      getPropsFromKeyField
    );

    return (
      <FormProvider {...methods}>
        {!isMappingModalOpen ? (
          <form
            className={`manual-ticket-form ${
              wrapperClassName ? wrapperClassName : ""
            }`}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div
              className={`modal-scroll-area ${
                shouldShowFields && "showing-fields-mapping"
              }`}
            >
              {(ticketManagerProviders?.length ?? 0) > 1 && (
                <TicketProviders
                  keysObject={keysThatHoldsValue}
                  onTicketProviderChange={resetKeysStateVariables}
                  providersOptions={ticketManagerProviders}
                  // Children type `formMethods` as UseFormReturn<FieldValues>;
                  // runtime object is identical (RHF generics are invariant).
                  formMethods={
                    methods as unknown as UseFormReturn<FieldValues>
                  }
                  withMarginBottom={false}
                />
              )}
              {!selectedTicketProvider ? (
                <Loader
                  placeholder
                  center
                  recolor
                  iconSize={"xxl"}
                  img={getSvg("icon-notebook")}
                  subtitle={"Select an Instance to continue"}
                />
              ) : keysError ? (
                <Loader center subtitle={keysError?.message} />
              ) : (
                <>
                  <TicketEndpointKeys
                    ticketProviderId={selectedTicketProvider}
                    onGetProps={setGetPropsFromKeyField}
                    onKeyChange={() => {
                      resetKeysStateVariables();
                      // Cleared to undefined (was null) so fields refetch from the
                      // updated keys rather than the prior fieldMappingId.
                      setFieldMappingProp(undefined);
                    }}
                    onError={setKeysError}
                    onFinalPhase={(isFinal?: boolean, hasMapping?: boolean) => {
                      setKeysFinalPhase(!!isFinal);
                      setKeysHasMapping(!!hasMapping);
                    }}
                    keysObject={keysThatHoldsValue}
                    formMethods={
                      methods as unknown as UseFormReturn<FieldValues>
                    }
                    externalEndpointId={externalEndpointId}
                  />
                  {isEditMapping ? (
                    <AlertIndicator
                      type={alertTypes.SUCCESS}
                      content={
                        <div className={"alert-indicator-content-inner"}>
                          <div className={"alert-indicator-content-left"}>
                            {`There is an existing Field Mapping for `}
                            {!!keysDisplayForMapping?.length ? (
                              keysDisplayForMapping?.map(
                                (keyDisplay, index) => (
                                  <span key={`${keyDisplay}--${index}`}>
                                    <span className={"modal-live-text"}>
                                      {keyDisplay}
                                    </span>
                                    {index !==
                                      keysDisplayForMapping?.length - 1 &&
                                      " and "}
                                  </span>
                                )
                              )
                            ) : (
                              <span className={"modal-live-text"}>
                                {ticketManagerFriendlyName}
                              </span>
                            )}
                            {`.`}
                          </div>
                          <div className={"alert-indicator-content-right"}>
                            <TextButton
                              data-testid={"mapping-indicator-action"}
                              type={"button"}
                              label={"Edit Mapping"}
                              buttonStyle={"textLink"}
                              onClick={() => {
                                onEventTriggered("Pane_Edit_Mapping");
                                setIsMappingModalOpen(true);
                              }}
                            />
                          </div>
                        </div>
                      }
                    />
                  ) : isCreateMapping && ticketManagerMustHaveMapping ? (
                    <Loader
                      center
                      flexcenter
                      iconSize={"xxl"}
                      largeTitle
                      icon={"seem-icon-notebook"}
                      title="No Mapping Found"
                      subtitle={`Map the fields from ${systemLabel} to your ticketing system to align the way they are presented`}
                      largeButtons
                      action={{
                        label: "Create Mapping",
                        onClick: () => {
                          onEventTriggered("Pane_Create_Mapping");
                          setIsMappingModalOpen(true);
                        },
                      }}
                    />
                  ) : isCreateMapping && !ticketManagerMustHaveMapping ? (
                    <AlertIndicator
                      type={alertTypes.GENERAL}
                      content={
                        <div className={"alert-indicator-content-inner"}>
                          <div className={"alert-indicator-content-left"}>
                            <div
                              className={
                                "alert-indicator-content-text-and-button-wrap"
                              }
                            >
                              <div>{`No Mapping configured (Optional).`}</div>
                            </div>
                          </div>
                          <div className={"alert-indicator-content-right"}>
                            <TextButton
                              data-testid={"mapping-indicator-action"}
                              type={"button"}
                              label={"Click to configure"}
                              buttonStyle={"textLink"}
                              onClick={() => {
                                onEventTriggered("Pane_Configure_Mapping");
                                setIsMappingModalOpen(true);
                              }}
                            />
                          </div>
                        </div>
                      }
                    />
                  ) : (
                    <></>
                  )}
                  {shouldShowSeparator && (
                    <div
                      className={"sections-separator"}
                      style={{ marginTop: "15px", marginBottom: "5px" }}
                    />
                  )}
                  {shouldShowKeysPlaceholder ? (
                    <Loader
                      placeholder
                      center
                      recolor
                      iconSize={"xxl"}
                      img={getSvg("icon-notebook")}
                      subtitle={`Select ${getAorAN(
                        firstUndefinedKey as string
                      )} to continue`}
                    />
                  ) : shouldShowKeysLoader ? (
                    <Loader
                      center
                      className="is-spinner"
                      img={getSvg("icon-spinner")}
                      iconSize="xxxl"
                      title={"Retrieving your mapping data fields"}
                      subtitle={"This may take a few moments"}
                    />
                  ) : shouldShowFields ? (
                    <>
                      {fieldsReady && (
                        <>
                          <div className={"mapped-fields-title"}>
                            <img
                              className={"ticket-manager-logo"}
                              src={getDataSourceIcon(
                                ticketManagerId as string,
                                "S"
                              )}
                              alt={ticketManagerFriendlyName}
                            />
                            <h3>Mapped Fields Values</h3>
                          </div>
                          <div className="mapped-fields-description">{`Map Findings attributes to ticket fields by applying a template or manually inserting attributes with { or typing custom text.`}</div>
                        </>
                      )}
                      <TicketEndpointFields
                        keysObject={keysThatHoldsValue}
                        fieldMappingId={fieldMappingProp}
                        onData={() => {
                          setFieldsReady(true);
                        }}
                        filters_config={filters_config}
                        filters_config_for_dynamic_mapping={filters_config}
                        onLoading={setFieldsLoading}
                        ticketProviderId={selectedTicketProvider}
                        onGetProps={
                          setGetPropsFromField as (getProps: unknown) => void
                        }
                        context={context}
                        formMethods={
                          methods as unknown as UseFormReturn<FieldValues>
                        }
                        // `initialValues`/`endpointConfig` arrive as `unknown`
                        // from untyped .js callers; narrowed to the field
                        // component's prop types (runtime shape unchanged).
                        initialValues={
                          initialValues as
                            | Record<string, unknown>
                            | null
                            | undefined
                        }
                        disableInitialValues={disableInitialValues}
                        externalEndpointId={externalEndpointId}
                        fetchPolicy={fetchPolicy}
                        endpointConfig={endpointConfig as EndpointConfig | undefined}
                      />
                      {fieldsReady && additionalPreferences}
                    </>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </div>
            {errorsContainer}
            {modalButtonsContainer}
          </form>
        ) : (
          <>
            <TextButton
              data-testid={"back-to-btn"}
              className={"modal-back-button"}
              type={"button"}
              leftIcon={"seem-icon-left-arrow"}
              label={"Back"}
              buttonStyle={"textLink"}
              onClick={() => setIsMappingModalOpen(false)}
            />
            <div className={"manual-ticket-form-wrap"}>
              <h3 className={"margin-bottom-10"}>{`${
                isEditMapping ? "Edit" : "Create"
              } Mapping`}</h3>
              <TicketEndpointColumns
                keysObject={keysThatHoldsValue}
                systemLabel={systemLabel}
                getPropsFromKeyField={getPropsFromKeyField}
                ticketProviderId={selectedTicketProvider ?? undefined}
                ticketManagerId={ticketManagerId}
                ticketManagerFriendlyName={ticketManagerFriendlyName}
                mode={
                  isEditMapping
                    ? ticketEndpointColumnsModesEnum.EDIT
                    : ticketEndpointColumnsModesEnum.CREATE
                }
                onClose={() => setIsMappingModalOpen(false)}
                ticketManagerMustHaveColumnsMapping={
                  ticketManagerMustHaveColumnsMapping
                }
                onEventTriggered={onEventTriggered}
                onCreateMapping={onCreateMapping}
                onCreateMappingError={onCreateMappingError}
                client={client}
              />
            </div>
          </>
        )}
      </FormProvider>
    );
  }
);

export default TicketTemplate;
